import {find, map} from 'lodash';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {InformationModal} from 'components';
import {ClientConnectFieldPath} from 'constants/client-connect';
import {AccountAssignmentInfo, CustomField} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {ImportError, FieldValidationError, FieldValidationErrorMap, ClientConnect} from 'types';
import {apiUtils, userInfoUtils} from 'utils';

type Props = {
    open: boolean;
    errors: ImportError[];
    importedConnects: ClientConnect[];
    getAssignmentUserInfoFields: (assignment: AccountAssignmentInfo) => CustomField[];
    onClose: () => void;
};

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS;
const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;
const ASSIGNMENT_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.ASSIGNMENT;

// Index starts from 0 + first row in excel file is headers so the real row number is index + 2
const ROW_DELTA = 2;

const T_KEY_ERROR = {
    [ClientConnectFieldPath.ID]: SESSION_T_KEYS.ID,
    [ClientConnectFieldPath.FIRST_NAME]: SESSION_T_KEYS.FIRST_NAME,
    [ClientConnectFieldPath.LAST_NAME]: SESSION_T_KEYS.LAST_NAME,
    [ClientConnectFieldPath.GROUP]: ASSIGNMENT_T_KEYS.GROUP,
    [ClientConnectFieldPath.SUBGROUP]: ASSIGNMENT_T_KEYS.SUBGROUP,
    [ClientConnectFieldPath.AGENT_EMAIL]: SESSION_T_KEYS.AGENT,
    [ClientConnectFieldPath.COMMENTS]: SESSION_T_KEYS.COMMENTS,
    [ClientConnectFieldPath.PHONE]: SESSION_T_KEYS.PHONE,
    [ClientConnectFieldPath.EMAIL]: SESSION_T_KEYS.EMAIL
};

export const ImportErrorsDialog: React.FC<Props> = ({open, errors, importedConnects, getAssignmentUserInfoFields, onClose}: Props) => {
    const {t} = useTranslation();

    const getControlOnlyFields = useCallback((connect: ClientConnect) => {
        if (!connect) {
            return null;
        }

        const fields = getAssignmentUserInfoFields(connect?.assignmentInfo);
        return userInfoUtils.getControlOnlyFields(fields);
    }, [getAssignmentUserInfoFields]);

    const getErrorMessages = useCallback((importError: ImportError): {field: string, message: string}[] => {
        const importErrors: FieldValidationErrorMap = importError.error?.data?.errors;
        const connect = importedConnects?.[importError.index];
        const controlOnlyFields = getControlOnlyFields(connect);
        return map(importErrors, (error: FieldValidationError, field: string) => {
            const fieldTKey = T_KEY_ERROR[field];
            let fieldName = null;
            if (fieldTKey) {
                fieldName = t(fieldTKey);
            }
            if (!fieldName && field.startsWith(ClientConnectFieldPath.CONTROL_ONLY_INFO)) {
                let exactField = field.replace(ClientConnectFieldPath.CONTROL_ONLY_INFO, '');
                if (exactField.startsWith('.')) {
                    exactField = exactField.substring(1);
                }
                const controlOnlyField = find(controlOnlyFields, {name: exactField});
                if (controlOnlyField) {
                    fieldName = controlOnlyField.title;
                } else {
                    fieldName = t(SESSION_T_KEYS.CONTROL_ONLY_INFO);
                    if (exactField) {
                        fieldName += ` (${exactField})`;
                    }
                }
            }
            if (!fieldName) {
                fieldName = field;
            }
            return {field: fieldName, message: apiUtils.getErrorMessageByCode(t, error)};
        });
    }, [t, importedConnects, getControlOnlyFields]);

    return (
        <InformationModal
            open={open}
            title={t(T_KEYS.IMPORT_ERRORS_DIALOG.TITLE)}
            fullWidth
            maxWidth='sm'
            onClose={onClose}
        >
            {map(errors, (bulkError) => (
                <div key={bulkError.index} className='tw-text-alert tw-p-4'>
                    <div className='tw-ts-h5'>
                        {`${t(T_KEYS.IMPORT_ERRORS_DIALOG.ROW_ERROR_TITLE)} ${bulkError.index + ROW_DELTA}`}
                    </div>
                    <div>
                        {getErrorMessages(bulkError).map(
                            ({field, message}) => (
                                <div key={field}>
                                    <b>{field}</b>
                                    {' - '}
                                    {message}
                                </div>
                            ))}
                    </div>
                </div>
            ))}
        </InformationModal>
    );
};
